/**
 * ImportDialog - generisk dialog for filimport (.xlsx/.csv) med forhåndsvisning.
 * Servertekst vises alltid som tekst. Se backend/shared/import/README.md.
 */
import React, { useEffect, useRef } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useImport } from '@/hooks/useImport';
import type { ImportAPI } from '@/services/api/import.api';
import type { ImportForhandsvisning } from '@/services/api/types';
import { lagMalFil } from './lagMalFil';
import ImportFeilListe from './ImportFeilListe';
import ImportForhandsvisningTabell, { type ImportKolonne } from './ImportForhandsvisningTabell';

export type { ImportKolonne };

export interface ImportDialogProps<R, Res> {
  open: boolean;
  onClose: () => void;
  onImportert: () => void;
  tittel: string;
  hjelpetekst: string;
  api: ImportAPI<R, Res>;
  kolonner: ImportKolonne[];
  oppsummering?: (forhandsvisning: ImportForhandsvisning<R>, valgte: ImportForhandsvisning<R>['rader']) => React.ReactNode;
  formaterResultat: (resultat: Res) => string;
  malFilnavn: string;
  /** Maks antall datarader i filen; standard 2000 (talentimporten). */
  maksRader?: number;
}

interface VisningInput<F> {
  steg: string;
  laster: boolean;
  forhandsvisning: F | null | undefined;
  feilAntall: number;
}

/** Avledede visningsflagg, samlet utenfor komponenten for å holde den enkel. */
const utledVisning = <F extends { rader: unknown[] }>({ steg, laster, forhandsvisning, feilAntall }: VisningInput<F>) => ({
  tomFil: !!forhandsvisning && forhandsvisning.rader.length === 0 && feilAntall === 0,
  opptatt: laster || steg === 'importerer',
  /** Forhåndsvisningen når den skal vises, ellers null. */
  synligForhandsvisning: steg === 'forhandsvis' || steg === 'importerer' ? (forhandsvisning ?? null) : null,
});

const ImportDialog = <R, Res>({
  open,
  onClose,
  onImportert,
  tittel,
  hjelpetekst,
  api,
  kolonner,
  oppsummering,
  formaterResultat,
  malFilnavn,
  maksRader,
}: ImportDialogProps<R, Res>) => {
  const { steg, laster, forhandsvisning, feil, valgte, velgbare, feilRadnr, velgFil, bekreft, veksleRad, veksleAlle, tilbakestill } =
    useImport<R, Res>(api, kolonner, { maksRader });
  const filRef = useRef<HTMLInputElement>(null);
  const malLasterRef = useRef(false);

  useEffect(() => {
    if (!open) tilbakestill();
  }, [open, tilbakestill]);

  const handleFil = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fil = e.target.files?.[0];
    if (fil) void velgFil(fil);
    if (filRef.current) filRef.current.value = '';
  };

  const handleMal = async () => {
    if (malLasterRef.current) return;
    malLasterRef.current = true;
    try {
      await lagMalFil(await api.hentMal(), malFilnavn);
    } catch {
      toast.error('Kunne ikke laste ned malen.');
    } finally {
      malLasterRef.current = false;
    }
  };

  const handleBekreft = async () => {
    const resultat = await bekreft();
    if (resultat === null) return;
    toast.success(formaterResultat(resultat));
    onImportert();
    onClose();
  };

  // Ingen lukking via Escape eller klikk utenfor mens importen pågår.
  const laas = (e: Event) => {
    if (steg === 'importerer') e.preventDefault();
  };
  // Feil uten radnr (filnivå eller mislykket bekreft) stopper importen; feil på enkeltrader gjør det ikke.
  const filFeil = feil.some((f) => f.radnr === undefined);
  const { tomFil, opptatt, synligForhandsvisning } = utledVisning({ steg, laster, forhandsvisning, feilAntall: feil.length });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-3xl grid-cols-1 overflow-y-auto overflow-x-hidden"
        onEscapeKeyDown={laas}
        onPointerDownOutside={laas}
        onInteractOutside={laas}
      >
        <DialogHeader>
          <DialogTitle>{tittel}</DialogTitle>
          <DialogDescription>{hjelpetekst}</DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-4">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <Button type="button" variant="outline" size="sm" onClick={handleMal}>
              <Download className="mr-2 h-4 w-4" />
              Last ned mal
            </Button>
            <input
              ref={filRef}
              type="file"
              accept=".xlsx,.csv"
              aria-label="Velg fil"
              disabled={opptatt}
              onChange={handleFil}
              className="min-w-0 max-w-full text-sm"
            />
            {opptatt && <Loader2 className="h-4 w-4 animate-spin" role="status" aria-label="Laster" />}
          </div>

          {feil.length > 0 && <ImportFeilListe feil={feil} />}
          {tomFil && steg === 'forhandsvis' && (
            <p className="text-sm text-muted-foreground">Filen har ingen rader å importere</p>
          )}
          {synligForhandsvisning && (
            <ImportForhandsvisningTabell
              data={synligForhandsvisning}
              kolonner={kolonner}
              valgte={valgte}
              velgbare={velgbare}
              feilRadnr={feilRadnr}
              onVeksleRad={veksleRad}
              onVeksleAlle={veksleAlle}
              oppsummering={oppsummering}
            />
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={steg === 'importerer'}>
            Avbryt
          </Button>
          {synligForhandsvisning && (
            <Button type="button" onClick={handleBekreft} disabled={opptatt || filFeil || valgte.size === 0}>
              {steg === 'importerer' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {`Importer ${valgte.size} ${valgte.size === 1 ? 'rad' : 'rader'}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
